(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initMenu = () => {
    const toggle = document.querySelector('[data-menu-toggle]');
    const nav = document.querySelector('[data-main-nav]');
    if (!toggle || !nav || toggle.dataset.ready === 'true') return;
    toggle.dataset.ready = 'true';
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      nav.classList.toggle('is-open', !open);
      document.body.classList.toggle('menu-open', !open);
    });
  };

  const initReveal = () => {
    const els = [...document.querySelectorAll('[data-reveal]:not([data-reveal-ready])')];
    els.forEach(el => el.dataset.revealReady = 'true');
    if (!els.length || reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: .12, rootMargin: '0px 0px -40px' });
    els.forEach(el => io.observe(el));
  };

  const initTilt = () => {
    if (reducedMotion || !window.matchMedia('(pointer:fine)').matches) return;
    document.querySelectorAll('[data-tilt]:not([data-tilt-ready])').forEach(el => {
      el.dataset.tiltReady = 'true';
      const max = Number(el.dataset.tiltMax || 6);
      const reset = () => { el.style.setProperty('--rx', '0deg'); el.style.setProperty('--ry', '0deg'); };
      el.addEventListener('pointermove', e => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - .5;
        const y = (e.clientY - r.top) / r.height - .5;
        el.style.setProperty('--rx', `${(-y * max * 2).toFixed(2)}deg`);
        el.style.setProperty('--ry', `${(x * max * 2).toFixed(2)}deg`);
      });
      el.addEventListener('pointerleave', reset);
    });
  };

  const initMask3D = () => {
    if (reducedMotion) return;
    document.querySelectorAll('[data-mask-3d]:not([data-mask-ready])').forEach(scene => {
      scene.dataset.maskReady = 'true';
      const max = Number(scene.dataset.tiltMax || 14);
      const object = scene.querySelector('.mask-3d-object') || scene;
      let targetX = 0, targetY = 0, currentX = 0, currentY = 0, raf = 0;
      const fine = window.matchMedia('(pointer:fine)').matches;

      const render = () => {
        currentX += (targetX - currentX) * .085;
        currentY += (targetY - currentY) * .085;
        object.style.setProperty('--mask-rx', `${currentY.toFixed(2)}deg`);
        object.style.setProperty('--mask-ry', `${currentX.toFixed(2)}deg`);
        object.style.setProperty('--mask-x', `${(currentX * .65).toFixed(2)}px`);
        object.style.setProperty('--mask-y', `${(currentY * -.35).toFixed(2)}px`);
        if (Math.abs(targetX-currentX) > .01 || Math.abs(targetY-currentY) > .01) raf = requestAnimationFrame(render);
        else raf = 0;
      };
      const schedule = () => { if (!raf) raf = requestAnimationFrame(render); };

      if (fine) {
        scene.addEventListener('pointermove', e => {
          const r = scene.getBoundingClientRect();
          const nx = ((e.clientX - r.left) / r.width - .5) * 2;
          const ny = ((e.clientY - r.top) / r.height - .5) * 2;
          targetX = nx * max;
          targetY = -ny * max * .72;
          scene.style.setProperty('--glare-x', `${((nx + 1) * 50).toFixed(0)}%`);
          scene.style.setProperty('--glare-y', `${((ny + 1) * 50).toFixed(0)}%`);
          schedule();
        });
        scene.addEventListener('pointerleave', () => { targetX = 0; targetY = 0; schedule(); });
      }
    });
  };

  const activateMedia = (root, id) => {
    if (!id) return;
    root.querySelectorAll('[data-media-id]').forEach(el => el.classList.toggle('is-active', String(el.dataset.mediaId) === String(id)));
    root.querySelectorAll('[data-media-target]').forEach(el => el.classList.toggle('is-active', String(el.dataset.mediaTarget) === String(id)));
  };

  const updateStockUrgency = (root, opt) => {
    const box = root.querySelector('[data-stock-urgency]');
    if (!box || !opt) return;
    const message = box.querySelector('[data-stock-message]');
    const available = opt.dataset.available === 'true';
    const managed = opt.dataset.inventoryManaged === 'true';
    const policy = opt.dataset.inventoryPolicy;
    const qty = Number(opt.dataset.inventory || 0);
    const threshold = Number(box.dataset.threshold || 8);
    box.classList.toggle('is-soldout', !available);
    box.classList.toggle('is-low', available && managed && policy !== 'continue' && qty > 0 && qty <= threshold);
    if (!message) return;
    if (!available) message.innerHTML = 'Agotado temporalmente';
    else if (managed && policy !== 'continue' && qty > 0 && qty <= threshold) message.innerHTML = `Solo quedan <strong>${qty}</strong> unidades disponibles`;
    else message.innerHTML = 'En stock · puedes pedirlo ahora';
  };

  const initProduct = () => {
    document.querySelectorAll('[data-product-root]:not([data-product-ready])').forEach(root => {
      root.dataset.productReady = 'true';
      root.querySelectorAll('[data-media-target]').forEach(button => button.addEventListener('click', () => activateMedia(root, button.dataset.mediaTarget)));

      const select = root.querySelector('[data-variant-select]');
      const price = root.querySelector('[data-product-price]');
      const compare = root.querySelector('[data-product-compare]');
      const mobilePrice = root.querySelector('[data-mobile-price]');
      const salePill = root.querySelector('[data-sale-pill]');
      const salePercent = root.querySelector('[data-sale-percent]');
      const add = root.querySelector('[data-add-button]');
      const addLabel = root.querySelector('[data-add-label]');
      if (select) {
        select.addEventListener('change', () => {
          const opt = select.selectedOptions[0];
          if (!opt) return;
          if (price) price.textContent = opt.dataset.price || '';
          if (mobilePrice) mobilePrice.textContent = opt.dataset.price || '';
          if (compare) {
            const hasCompare = Boolean(opt.dataset.compare);
            compare.hidden = !hasCompare;
            compare.textContent = opt.dataset.compare || '';
          }
          const pct = Number(opt.dataset.salePercent || 0);
          if (salePill) salePill.hidden = pct <= 0;
          if (salePercent) salePercent.textContent = String(pct);
          const available = opt.dataset.available === 'true';
          if (add) add.disabled = !available;
          if (addLabel) addLabel.textContent = available ? (addLabel.dataset.default || 'Comprar ahora · Añadir al carrito') : 'Agotado';
          updateStockUrgency(root, opt);
          if (opt.dataset.media) activateMedia(root, opt.dataset.media);
          const url = new URL(window.location.href);
          url.searchParams.set('variant', opt.value);
          history.replaceState({}, '', url.toString());
        });
        if (addLabel) addLabel.dataset.default = addLabel.textContent.trim();
      }

      const qty = root.querySelector('[data-qty-input]');
      root.querySelector('[data-qty-minus]')?.addEventListener('click', () => { qty.value = Math.max(1, Number(qty.value || 1) - 1); });
      root.querySelector('[data-qty-plus]')?.addEventListener('click', () => { qty.value = Math.max(1, Number(qty.value || 1) + 1); });

      root.querySelector('[data-scroll-buy]')?.addEventListener('click', () => {
        const form = root.querySelector('.product-form');
        if (!form) return;
        form.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
        setTimeout(() => root.querySelector('[data-add-button]')?.focus(), reducedMotion ? 0 : 450);
      });
    });
  };

  const initCountdowns = () => {
    document.querySelectorAll('[data-countdown]:not([data-countdown-ready])').forEach(box => {
      box.dataset.countdownReady = 'true';
      const end = new Date(box.dataset.countdownEnd || '');
      if (Number.isNaN(end.getTime())) { box.hidden = true; return; }
      const fields = {
        days: box.querySelector('[data-days]'),
        hours: box.querySelector('[data-hours]'),
        minutes: box.querySelector('[data-minutes]'),
        seconds: box.querySelector('[data-seconds]')
      };
      const tick = () => {
        const left = end.getTime() - Date.now();
        if (left <= 0) { box.hidden = true; return false; }
        const d = Math.floor(left / 86400000);
        const h = Math.floor((left % 86400000) / 3600000);
        const m = Math.floor((left % 3600000) / 60000);
        const s = Math.floor((left % 60000) / 1000);
        if (fields.days) fields.days.textContent = String(d).padStart(2,'0');
        if (fields.hours) fields.hours.textContent = String(h).padStart(2,'0');
        if (fields.minutes) fields.minutes.textContent = String(m).padStart(2,'0');
        if (fields.seconds) fields.seconds.textContent = String(s).padStart(2,'0');
        return true;
      };
      if (tick()) box._countdownTimer = setInterval(() => { if (!tick()) clearInterval(box._countdownTimer); }, 1000);
    });
  };

  const initFaq = () => {
    document.querySelectorAll('.faq-list:not([data-faq-ready])').forEach(list => {
      list.dataset.faqReady = 'true';
      list.querySelectorAll('details').forEach(item => item.addEventListener('toggle', () => {
        if (!item.open) return;
        list.querySelectorAll('details').forEach(other => { if (other !== item) other.removeAttribute('open'); });
      }));
    });
  };

  const boot = () => { initMenu(); initReveal(); initTilt(); initMask3D(); initProduct(); initCountdowns(); initFaq(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
